import { doc, getDoc } from "firebase/firestore";
import { firestoreDb } from "../firebase";
import fleetDashboardFallback from "../layouts/fallback/fleet-dashboard.json";
import schoolDashboardFallback from "../layouts/fallback/school-dashboard.json";
import fleetVehiclesFallback from "../layouts/fallback/fleet-vehicles.json";
import schoolVehiclesFallback from "../layouts/fallback/school-vehicles.json";
import fleetRoutesFallback from "../layouts/fallback/fleet-routes.json";
import schoolRoutesFallback from "../layouts/fallback/school-routes.json";
import fleetStopsFallback from "../layouts/fallback/fleet-stops.json";
import schoolStopsFallback from "../layouts/fallback/school-stops.json";
import fleetDriversFallback from "../layouts/fallback/fleet-drivers.json";
import schoolDriversFallback from "../layouts/fallback/school-drivers.json";
import fleetUsersFallback from "../layouts/fallback/fleet-users.json";
import schoolUsersFallback from "../layouts/fallback/school-users.json";
import schoolAttendantsFallback from "../layouts/fallback/school-attendants.json";
import fleetAttendantsFallback from "../layouts/fallback/fleet-attendants.json";
import fleetSitesFallback from "../layouts/fallback/fleet-sites.json";
import schoolSitesFallback from "../layouts/fallback/school-sites.json";
import fleetSchedulesFallback from "../layouts/fallback/fleet-schedules.json";
import schoolSchedulesFallback from "../layouts/fallback/school-schedules.json";
import fleetSettingsFallback from "../layouts/fallback/fleet-settings.json";
import schoolSettingsFallback from "../layouts/fallback/school-settings.json";
import fleetReportsFallback from "../layouts/fallback/fleet-reports.json";
import schoolReportsFallback from "../layouts/fallback/school-reports.json";
import fleetAlertsFallback from "../layouts/fallback/fleet-alerts.json";
import schoolAlertsFallback from "../layouts/fallback/school-alerts.json";
import fleetDevicesFallback from "../layouts/fallback/fleet-devices.json";
import schoolDevicesFallback from "../layouts/fallback/school-devices.json";
import fleetNavFallback from "../layouts/fallback/fleet-nav.json";
import schoolNavFallback from "../layouts/fallback/school-nav.json";
import { sanitizeLayout, validateLayout } from "../layout/validateLayout";
import { validateModuleLayout } from "../layout/validateModuleLayout";
import { validateSettingsLayout } from "../layout/validateSettingsLayout";
import { validateReportsLayout } from "../layout/validateReportsLayout";
import { validateAlertsLayout } from "../layout/validateAlertsLayout";
import { validateNavLayout } from "../layout/validateNavLayout";
import { logLayoutEvent, logLayoutWarning } from "../utils/uiLayoutLogger";

const CACHE_TTL_MS = 15 * 60 * 1000;
const memoryCache = new Map();

function buildCacheKey(organizationId, pageId) {
  return `uiLayout:${organizationId}:${pageId}`;
}

const FIRESTORE_LAYOUT_TIMEOUT_MS = 2500;

const BUNDLED_LAYOUTS = {
  "fleet-dashboard": fleetDashboardFallback,
  "school-dashboard": schoolDashboardFallback,
  "fleet-vehicles": fleetVehiclesFallback,
  "school-vehicles": schoolVehiclesFallback,
  "fleet-routes": fleetRoutesFallback,
  "school-routes": schoolRoutesFallback,
  "fleet-stops": fleetStopsFallback,
  "school-stops": schoolStopsFallback,
  "fleet-drivers": fleetDriversFallback,
  "school-drivers": schoolDriversFallback,
  "fleet-users": fleetUsersFallback,
  "school-users": schoolUsersFallback,
  "school-attendants": schoolAttendantsFallback,
  "fleet-attendants": fleetAttendantsFallback,
  "fleet-sites": fleetSitesFallback,
  "school-sites": schoolSitesFallback,
  "fleet-schedules": fleetSchedulesFallback,
  "school-schedules": schoolSchedulesFallback,
  "fleet-settings": fleetSettingsFallback,
  "school-settings": schoolSettingsFallback,
  "fleet-reports": fleetReportsFallback,
  "school-reports": schoolReportsFallback,
  "fleet-alerts": fleetAlertsFallback,
  "school-alerts": schoolAlertsFallback,
  "fleet-devices": fleetDevicesFallback,
  "school-devices": schoolDevicesFallback,
  "fleet-nav": fleetNavFallback,
  "school-nav": schoolNavFallback,
};

export function getBundledLayout(pageId) {
  return BUNDLED_LAYOUTS[pageId] || null;
}

function validateLayoutForPage(layout, pageId) {
  if (pageId?.endsWith("-dashboard")) {
    return validateLayout(layout);
  }
  if (pageId?.endsWith("-settings")) {
    return validateSettingsLayout(layout);
  }
  if (pageId?.endsWith("-reports")) {
    return validateReportsLayout(layout);
  }
  if (pageId?.endsWith("-alerts")) {
    return validateAlertsLayout(layout);
  }
  if (pageId?.endsWith("-nav")) {
    return validateNavLayout(layout);
  }
  return validateModuleLayout(layout);
}

function getFallbackLayout(pageId) {
  return getBundledLayout(pageId);
}

function getFirestoreLayoutWithTimeout(layoutDocRef, timeoutMs = FIRESTORE_LAYOUT_TIMEOUT_MS) {
  return Promise.race([
    getDoc(layoutDocRef),
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error("layout_firestore_timeout")), timeoutMs);
    }),
  ]);
}

function getCachedLayout(cacheKey) {
  const now = Date.now();
  const memoryEntry = memoryCache.get(cacheKey);
  if (memoryEntry && now - memoryEntry.cachedAt < CACHE_TTL_MS) {
    return memoryEntry.layout;
  }

  try {
    const sessionRaw = sessionStorage.getItem(cacheKey);
    if (!sessionRaw) return null;
    const sessionEntry = JSON.parse(sessionRaw);
    if (now - sessionEntry.cachedAt < CACHE_TTL_MS) {
      memoryCache.set(cacheKey, sessionEntry);
      return sessionEntry.layout;
    }
  } catch (error) {
    console.warn("[layout] Failed to parse cached layout:", error);
  }

  return null;
}

function setCachedLayout(cacheKey, layout) {
  const entry = { layout, cachedAt: Date.now() };
  memoryCache.set(cacheKey, entry);
  try {
    sessionStorage.setItem(cacheKey, JSON.stringify(entry));
  } catch (error) {
    console.warn("[layout] Failed to cache layout in sessionStorage:", error);
  }
}

function validateOrFallback(layout, pageId, source) {
  const sanitized = sanitizeLayout(layout);
  const validation = validateLayoutForPage(sanitized, pageId);
  if (validation.valid) {
    return { layout: sanitized, source, errors: [] };
  }

  const fallback = getFallbackLayout(pageId);
  if (!fallback) {
    return { layout: sanitized, source, errors: validation.errors };
  }

  logLayoutWarning("layout_invalid_using_fallback", { source, pageId, errors: validation.errors });
  return {
    layout: fallback,
    source: "fallback",
    errors: validation.errors,
  };
}

export function clearLayoutCache(organizationId, pageId) {
  const cacheKey = buildCacheKey(organizationId, pageId);
  memoryCache.delete(cacheKey);
  try {
    sessionStorage.removeItem(cacheKey);
  } catch (error) {
    logLayoutWarning("layout_cache_clear_failed", { cacheKey, error });
  }
}

export async function preloadDashboardLayout({ organizationId, pageId }) {
  return getDashboardLayout({ organizationId, pageId });
}

export async function getDashboardLayout({ organizationId, pageId }) {
  const cacheKey = buildCacheKey(organizationId, pageId);
  const cachedLayout = getCachedLayout(cacheKey);
  if (cachedLayout) {
    logLayoutEvent("layout_loaded", { pageId, source: "cache", organizationId });
    return { layout: cachedLayout, source: "cache", errors: [] };
  }

  const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;

  if (!isOffline) {
  try {
    const layoutDocRef = doc(firestoreDb, "organizations", String(organizationId), "uiLayouts", pageId);
    const snapshot = await getFirestoreLayoutWithTimeout(layoutDocRef);

    if (snapshot.exists()) {
      const { layout, source, errors } = validateOrFallback(snapshot.data(), pageId, "firestore");
      setCachedLayout(cacheKey, layout);
      logLayoutEvent("layout_loaded", { pageId, source, organizationId, errorsCount: errors.length });
      return { layout, source, errors };
    }
  } catch (error) {
    logLayoutWarning("layout_firestore_failed", { pageId, organizationId, error: error?.message });
  }
  } else {
    logLayoutEvent("layout_skip_firestore", { pageId, organizationId, reason: "offline" });
  }

  const fallback = getFallbackLayout(pageId);
  if (!fallback) {
    return { layout: null, source: "none", errors: [`No fallback found for pageId: ${pageId}`] };
  }

  const { layout, source, errors } = validateOrFallback(fallback, pageId, "fallback");
  setCachedLayout(cacheKey, layout);
  logLayoutEvent("layout_loaded", { pageId, source, organizationId, errorsCount: errors.length });
  return { layout, source, errors };
}
